import { Link, useNavigate } from "react-router";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { FormCreator, FormCreatorProps, FormFieldDef } from "@/components/shared/form-creator";
import { useAppDispatch, useAppSelector } from "@/lib/store";
import { loginUser } from "@/app/auth/login/login.slice";

const formSchema = z.object({
  email: z.string({ required_error: "Email is required" }).email("Invalid email address"),
  password: z.string({ required_error: "Password is required" }).min(6, "Password must be at least 6 characters"),
});

export function LoginForm() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { loading, error } = useAppSelector((state) => state.login);

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  });

  async function onSubmit(values: { email: string; password: string }) {
    await dispatch(loginUser(values)).unwrap();
    navigate("/");
  }

  const formDefinition: FormFieldDef[] = [
    {
      type: "email",
      name: "email",
      label: "Email",
      placeholder: "Enter your email",
      required: true,
    },
    {
      type: "password",
      name: "password",
      label: "Password",
      placeholder: "Enter your password",
      required: true,
      labelRight: (
        <Link to="/forgot-password" className="text-sm font-light">
          Forgot your password?
        </Link>
      ),
    },
  ];

  const formCreatorProps: FormCreatorProps = {
    form,
    onSubmit,
    formDefinition,
    buttonText: loading ? "Loading..." : "Login",
  };

  return (
    <>
      <div className="flex flex-col items-start gap-2 text-center mb-6">
        <h1 className="text-2xl font-bold">Login to your account</h1>
        <p className="text-balance text-sm text-muted-foreground">Enter your email below to login to your account</p>
      </div>

      <FormCreator {...formCreatorProps} />

      {/* TODO: Error formatting. SYN-61 */}
      {error && <p className="text-sm text-destructive text-center mt-4">{error}</p>}

      <div className="text-center text-sm mt-4">
        Don&apos;t have an account?{" "}
        <Link to="/signup" className="underline underline-offset-4">
          Sign up
        </Link>
      </div>
    </>
  );
}
